import {
  Button,
  FormButton,
  FormControl,
  openConfirmDialog,
  showNotification,
  Tooltip,
} from 'platform/components';
import {match} from 'ts-pattern';

import {useState} from 'react';
import {UseFormReturn} from 'react-hook-form';
import {useDispatch} from 'react-redux';

import {not} from 'ramda';
import {isTrue, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  documentContextApi,
  GetTireSetApiResponse,
  usePostTiresArchiveMutation,
  usePostTireSetsCancelTransitMutation,
  usePostTireSetsSetReadyMutation,
  usePostTireSetsSetRequestTransitMutation,
  usePostTireSetsSetTransitAcceptanceMutation,
  usePostTireSetsSetUnreadyMutation,
  usePostTiresIssueMutation,
  usePostTiresReceiptMutation,
  usePostTiresRestoreMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';

import {handleApiError} from '../../../utils/handleApiError';
import {printPDF} from '../../../utils/printPDF';
import {refreshDmsDatagrid} from '../../../utils/refreshDmsDatagrid';
import {TIRE_SET_DETAIL_TAB_IDS, TireSetDetailTabId} from '../constants/tireSetDetailTabIds';
import {useTireSetActions} from '../hooks/useTireSetActions';
import {TireSetDetailFormDefinition} from '../types/TireSetDetailFormDefinition';

interface EditTireSetActionsProps {
  tireOrderId: string;
  tireSetId: string;
  control: FormControl<TireSetDetailFormDefinition>;
  formApi: UseFormReturn<TireSetDetailFormDefinition>;
  onClose: () => void;
  onEdit?: () => void;
  saveTires: (data: TireSetDetailFormDefinition) => Promise<void>;
  tireSet: GetTireSetApiResponse;
  canEditTireSet: boolean;
  canReceiveTireSet: boolean;
  canIssueTireSet: boolean;
  canArchiveTireSet: boolean;
  homeBranchId: string | undefined;
  activeTabId: TireSetDetailTabId;
}

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function EditTireSetActions(props: EditTireSetActionsProps) {
  const dispatch = useDispatch();

  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  const {availableActions, getCheckedWheelIds, isFetching, refreshActions} = useTireSetActions({
    tireOrderId: props.tireOrderId,
    tireSetId: props.tireSetId,
    control: props.control,
    excludedKeys: [
      'transferToOrder',
      'printLabel',
      'printDeliveryNote',
      'request',
      'modifyRequest',
      'cancelRequest',
    ],
    homeBranchId: props.homeBranchId,
  });

  const [postTiresArchive] = usePostTiresArchiveMutation();
  const [postTiresReceipt, {isLoading: isTiresReceiptLoading}] = usePostTiresReceiptMutation();
  const [postTiresIssue, {isLoading: isTiresIssueLoading}] = usePostTiresIssueMutation();
  const [postTiresRestore, {isLoading: isTiresRestoreLoading}] = usePostTiresRestoreMutation();
  const [postTireSetsSetRequestTransit, {isLoading: isTireSetsSetRequestTransitLoading}] =
    usePostTireSetsSetRequestTransitMutation();
  const [postTireSetsCancelTransit, {isLoading: isTireSetsCancelTransitLoading}] =
    usePostTireSetsCancelTransitMutation();
  const [postTireSetsSetReady, {isLoading: isTiresSetReadyLoading}] =
    usePostTireSetsSetReadyMutation();
  const [postTireSetsSetUnready, {isLoading: isTiresSetUnreadyLoading}] =
    usePostTireSetsSetUnreadyMutation();
  const [postTireSetsSetTransitAcceptance, {isLoading: isTireSetsSetTransitAcceptanceLoading}] =
    usePostTireSetsSetTransitAcceptanceMutation();

  const refreshDocumentCount = () =>
    dispatch(
      documentContextApi.util.invalidateTags([{type: 'documentsCount', id: props.tireOrderId}])
    );

  const refreshDatagrids = () => {
    match(props.activeTabId)
      .with(TIRE_SET_DETAIL_TAB_IDS.TIRES, () => refreshDmsDatagrid('tire-order-set'))
      .with(TIRE_SET_DETAIL_TAB_IDS.TIRES_MOVEMENT, () => refreshDmsDatagrid('tire-set-movement'))
      .with(TIRE_SET_DETAIL_TAB_IDS.TIRES_ARCHIVE, () => refreshDmsDatagrid('tire-set-archive'))
      .exhaustive();
  };

  const receiptOfTireSet = () => {
    setLoadingAction('receipt');
    const assignMechanic = props.formApi.getValues('assignMechanic');

    props
      .saveTires(props.formApi.getValues())
      .then(() => {
        const selectedWheelIds = getCheckedWheelIds();
        return postTiresReceipt({
          orderId: props.tireOrderId,
          setId: props.tireSetId,
          body: {
            assignMechanic,
            wheels: selectedWheelIds.map((id) => ({id})),
            withoutPrintout: false,
          },
        }).unwrap();
      })
      .then((response) => {
        refreshDatagrids();
        props.onClose();
        printPDF(response?.printout?.pdfUrl ?? '');
      })
      .then(refreshDocumentCount)
      .then(() => props.onEdit && props.onEdit())
      .catch(handleApiError)
      .finally(() => setLoadingAction(null));
  };

  const issueOfTireSet = () => {
    setLoadingAction('issue');
    const assignMechanic = props.formApi.getValues('assignMechanic');

    props
      .saveTires(props.formApi.getValues())
      .then(() => {
        const selectedWheelIds = getCheckedWheelIds();
        return postTiresIssue({
          orderId: props.tireOrderId,
          setId: props.tireSetId,
          body: {assignMechanic, wheels: selectedWheelIds.map((id) => ({id}))},
        }).unwrap();
      })
      .then((response) => {
        refreshDatagrids();
        props.onClose();
        if (isNotNilOrEmpty(response?.printout?.pdfUrl)) {
          printPDF(response?.printout?.pdfUrl ?? '');
        }
      })
      .then(refreshDocumentCount)
      .then(() => props.onEdit && props.onEdit())
      .catch(handleApiError)
      .finally(() => setLoadingAction(null));
  };

  const archive = () => {
    setLoadingAction('archive');
    const assignMechanic = props.formApi.getValues('assignMechanic');

    props
      .saveTires(props.formApi.getValues())
      .then(() => {
        const selectedWheelIds = getCheckedWheelIds();

        openConfirmDialog({
          text: i18n.t('page.tiresInventory.labels.archiveTire'),
          onConfirm: () =>
            postTiresArchive({
              orderId: props.tireOrderId,
              setId: props.tireSetId,
              body: {assignMechanic, wheels: selectedWheelIds.map((id) => ({id}))},
            })
              .unwrap()
              .then((response) => {
                props.onClose();
                printPDF(response?.printout?.pdfUrl ?? '');
                refreshDatagrids();
              })
              .then(refreshDocumentCount)
              .then(() => props.onEdit && props.onEdit())
              .catch(handleApiError),
        });
      })
      .catch(handleApiError)
      .finally(() => setLoadingAction(null));
  };

  const restore = () => {
    const assignMechanic = props.formApi.getValues('assignMechanic');
    const selectedWheelIds = getCheckedWheelIds();

    openConfirmDialog({
      id: 'tireSetRestoreConfirm',
      text: i18n.t('page.tiresInventory.labels.restoreTire'),
      onConfirm: () =>
        postTiresRestore({
          orderId: props.tireOrderId,
          setId: props.tireSetId,
          body: {assignMechanic, wheels: selectedWheelIds.map((id) => ({id}))},
        })
          .unwrap()
          .then(() => {
            refreshDatagrids();
            props.onClose();
          })
          .then(refreshDocumentCount)
          .then(() => props.onEdit && props.onEdit())
          .catch(handleApiError),
    });
  };

  const handleSetRequestTransit = () => {
    postTireSetsSetRequestTransit({
      body: {
        tireSets: [
          {
            orderId: props.tireOrderId,
            setId: props.tireSetId,
          },
        ],
      },
    })
      .unwrap()
      .then((response) => {
        const url = response?.[0]?.printout?.pdfUrl;

        if (!url) {
          showNotification.error();
          return;
        }

        printPDF(url);

        refreshActions();
      })
      .then(refreshDocumentCount)
      .then(() => props.onEdit && props.onEdit())
      .catch(handleApiError);
  };

  const handleSetReady = () => {
    postTireSetsSetReady({
      body: {
        tireSets: [
          {
            tireOrderId: props.tireOrderId,
            tireSetId: props.tireSetId,
          },
        ],
      },
    })
      .unwrap()
      .then(() => {
        refreshActions();
      })
      .then(refreshDocumentCount)
      .then(() => props.onEdit && props.onEdit())
      .catch(handleApiError);
  };

  const handleSetTransitAcceptance = () => {
    postTireSetsSetTransitAcceptance({
      body: {
        tireSets: [
          {
            orderId: props.tireOrderId,
            setId: props.tireSetId,
          },
        ],
      },
    })
      .unwrap()
      .then(() => {
        refreshActions();
        refreshDmsDatagrid('tire-set-requests');
      })
      .then(refreshDocumentCount)
      .then(() => props.onEdit && props.onEdit())
      .catch(handleApiError);
  };

  const handleSetUnready = () => {
    postTireSetsSetUnready({
      body: {
        tireSets: [
          {
            tireOrderId: props.tireOrderId,
            tireSetId: props.tireSetId,
          },
        ],
      },
    })
      .unwrap()
      .then(() => {
        refreshActions();
      })
      .then(refreshDocumentCount)
      .then(() => props.onEdit && props.onEdit())
      .catch(handleApiError);
  };

  const handleCancelTransit = () => {
    postTireSetsCancelTransit({
      body: {
        tireSets: [
          {
            orderId: props.tireOrderId,
            setId: props.tireSetId,
          },
        ],
      },
    })
      .unwrap()
      .then(() => {
        refreshActions();
      })
      .then(refreshDocumentCount)
      .then(() => props.onEdit && props.onEdit())
      .catch(handleApiError);
  };

  const getActionHandler = (key: string) =>
    match(key)
      .with('receipt', () => receiptOfTireSet)
      .with('issue', () => issueOfTireSet)
      .with('archive', () => archive)
      .with('restore', () => restore)
      .with('transitRequest', () => handleSetRequestTransit)
      .with('acceptanceTransitRequest', () => handleSetTransitAcceptance)
      .with('prepareRequest', () => handleSetReady)
      .with('unprepareRequest', () => handleSetUnready)
      .with('cancelTransitRequest', () => handleCancelTransit)
      .otherwise(() => () => {});

  const getActionTitle = (key: string) =>
    match(key)
      .with('receipt', () => i18n.t('entity.tireSet.actions.receiptOfTireSet'))
      .with('issue', () => i18n.t('entity.tireSet.actions.issueOfTireSet'))
      .with('archive', () => i18n.t('general.actions.archive'))
      .with('restore', () => i18n.t('entity.tireSet.actions.restore'))
      .with('modifyRequest', () => i18n.t('entity.tireSet.actions.modifyRequest'))
      .with('transitRequest', () => i18n.t('entity.tireSet.actions.transitRequest'))
      .with('acceptanceTransitRequest', () =>
        i18n.t('entity.tireSet.actions.acceptanceTransitRequest')
      )
      .with('prepareRequest', () => i18n.t('entity.tireSet.actions.prepareRequest'))
      .with('unprepareRequest', () => i18n.t('entity.tireSet.actions.unprepareRequest'))
      .with('cancelTransitRequest', () => i18n.t('entity.tireSet.actions.cancelTransitRequest'))
      .otherwise(() => key);

  // Receipt, issue and archive save the form before acting, so they need EDIT on top of their own action.
  const getActionPermission = (key: string) =>
    match(key)
      .with('receipt', () => props.canReceiveTireSet && props.canEditTireSet)
      .with('issue', () => props.canIssueTireSet && props.canEditTireSet)
      .with('archive', () => props.canArchiveTireSet && props.canEditTireSet)
      .otherwise(() => props.canEditTireSet);

  const getActionVariant = (key: string) => {
    const primaryActions = ['receipt', 'issue', 'restore'];
    return primaryActions.includes(key) ? 'primary' : 'secondary';
  };

  const getActionLoading = (key: string) => {
    if (loadingAction === key) {
      return true;
    }

    return match(key)
      .with('receipt', () => isTiresReceiptLoading)
      .with('issue', () => isTiresIssueLoading)
      .with('restore', () => isTiresRestoreLoading)
      .with('transitRequest', () => isTireSetsSetRequestTransitLoading)
      .with('acceptanceTransitRequest', () => isTireSetsSetTransitAcceptanceLoading)
      .with('prepareRequest', () => isTiresSetReadyLoading)
      .with('unprepareRequest', () => isTiresSetUnreadyLoading)
      .with('cancelTransitRequest', () => isTireSetsCancelTransitLoading)
      .otherwise(() => false);
  };

  const hasDifferentWheelState = (states: string[]) => {
    const checkedWheels = props.formApi
      .getValues('wheels')
      .filter((wheel) => isTrue(wheel.checked));

    const wheelsWithoutId = checkedWheels.filter((wheel) => !wheel.id);
    const wheelIds = checkedWheels.filter((wheel) => !!wheel.id).map(({id}) => id);

    const selectedWheels = props.tireSet?.wheels?.filter(
      (wheel) => !!wheel?.id && wheelIds.includes(wheel.id)
    );

    if (selectedWheels?.length === 0 && wheelsWithoutId.length > 0) {
      return true;
    }

    return not(selectedWheels?.every((wheel) => states.includes(wheel?.state ?? '')));
  };

  const isTiresTabActive = props.activeTabId === TIRE_SET_DETAIL_TAB_IDS.TIRES;

  return (
    <>
      {availableActions.map((action: {key: string}) => (
        <Tooltip
          key={action.key}
          label={
            !getActionPermission(action.key) ? i18n.t('general.labels.noPermission') : undefined
          }
        >
          <Button
            title={getActionTitle(action.key)}
            variant={getActionVariant(action.key)}
            onClick={getActionHandler(action.key)}
            isDisabled={
              !getActionPermission(action.key) ||
              isFetching ||
              loadingAction !== null ||
              !isTiresTabActive ||
              props.formApi.formState.isSubmitting
            }
            isLoading={getActionLoading(action.key)}
          />
        </Tooltip>
      ))}

      <Tooltip label={!props.canEditTireSet ? i18n.t('general.labels.noPermission') : undefined}>
        <FormButton
          control={props.control}
          title={i18n.t('general.actions.saveAndClose')}
          variant="secondary"
          type="submit"
          isDisabled={
            not(hasDifferentWheelState(['ARCHIVED'])) ||
            !props.canEditTireSet ||
            isFetching ||
            loadingAction !== null ||
            !isTiresTabActive
          }
        />
      </Tooltip>
    </>
  );
}
